// R115 guest — the Calendar's reservation + listing actions (REQUESTS B1 B2 B3
// B4 B5 B7 B8), registered against the hub contract (../registry). Imported by
// ONE line in ../features.ts; it never touches the page's render loop.
//
// Registers (CALENDAR_CONTRACT ownership table, guest rows):
//   layers   scores          — Airbnb ★ per listing (server provider 'scores') → row badge when n ≥ 3
//            special_offers  — the Special Offers page's flag per Airbnb stay (server provider
//                              'special_offers', cache-only) → bar glyph / decoration
//   stat     special_offers  — "Special offers" KPI/filter (flagged stays in view; not in a preset yet)
//   stay     actions  message · recovery · claim · resolution (fixed order; all office roles)
//            sections special_offer · recovery · message
//   listing  sections score · counts · links;  actions resolution · claim
//   busy     'guest' — a dirty / in-flight form sheet vetoes the auto-refresh
//   css      'guest'
import {
  registerLayer, registerStat, registerSection, registerAction, registerBusy, registerCss,
  type CalendarCtx, type EntityRef, type BadgeSpec, type StayDecoration, type OfficeRole,
} from '../registry';
import type { Property, Stay } from '../types';
import { stayActions, scoreBadge, soMark } from '../../../../../lib/calendarActions.mjs';
import { GUEST_CSS } from './css';
import { stayFrom, detailFrom, propertyOf, confirmationOf } from './util';
import type { ScoresLayer, SoLayer } from './util';
import { openResolutionSheet, openClaimSheet, openRecoverySheet, openMessageSheet, sheetBusy } from './sheets';
import { specialOfferSection, recoverySection, messageSection, listingScoreSection, listingCountsSection, listingLinksSection, soFor } from './sections';

// ── layers ──────────────────────────────────────────────────────────────
// R115 integ: office roles like every other module's layer (the server providers are office-only too)
const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];
registerLayer({
  id: 'scores', label: 'Review scores (Airbnb ★)', swatch: 'var(--warning)', scope: 'row', server: 'scores', roles: OFFICE,
  badges: (p: Property, ctx: CalendarCtx): BadgeSpec[] => {
    const l = ctx.layer<ScoresLayer>('scores');
    const b = scoreBadge(l && l.by_listing ? l.by_listing[p.listing_id] : null);
    return b ? [{ key: 'score', glyph: '★', text: b.text, title: b.title, tone: b.tone, entity: { kind: 'listing', id: p.listing_id } }] : [];
  },
  count: (ctx) => {
    const l = ctx.layer<ScoresLayer>('scores');
    if (!l || !l.by_listing) return null;
    return ctx.data.properties.filter(p => scoreBadge(l.by_listing![p.listing_id])).length;
  },
});
registerLayer({
  id: 'special_offers', label: 'Special offers', swatch: 'var(--warning)', scope: 'stay', server: 'special_offers', roles: OFFICE,
  decorateStay: (s: Stay, ctx: CalendarCtx): StayDecoration | null => {
    const l = ctx.layer<SoLayer>('special_offers');
    const m = soMark(l && l.by_stay ? l.by_stay[s.id] : null);
    if (!m || !m.bar) return null;
    return {
      cls: m.tone === 'warn' ? 'gx-so-flag' : undefined,
      badges: [{ key: 'so', glyph: m.glyph || '⚑', title: m.title, tone: m.tone === 'warn' ? 'warn' : 'muted' }],
      titleSuffix: m.tone === 'warn' ? ' · ⚑ special offer' : ' · special offer (fees recorded)',
    };
  },
  count: (ctx) => {
    const l = ctx.layer<SoLayer>('special_offers');
    if (!l || l.unavailable || !l.by_stay) return null;
    return Object.values(l.by_stay).filter(x => x && x.state === 'flagged').length;
  },
});

// ── stat (a filter) — offered to the orchestrator for the Reservations preset ──
const flaggedIn = (s: Stay, ctx: CalendarCtx) => {
  const l = ctx.layer<SoLayer>('special_offers');
  return !!(l && l.by_stay && l.by_stay[s.id] && l.by_stay[s.id].state === 'flagged');
};
registerStat({
  key: 'special_offers', label: 'Special offers', icon: '⚑', layer: 'special_offers',
  compute: (ctx) => {
    const l = ctx.layer<SoLayer>('special_offers');
    if (!l || l.unavailable) return null;
    const n = ctx.data.stays.filter(s => flaggedIn(s, ctx)).length;
    return { value: n, tone: n ? 'warn' : undefined, sub: n ? 'fees not broken out' : 'all broken out' };
  },
  match: { stays: (s, ctx) => flaggedIn(s, ctx) },
});

// ── stay actions: fixed order Message · Recovery · Claim · Resolution ──
const stayCtx = (ref: EntityRef, data: unknown, ctx: CalendarCtx) => {
  const s = stayFrom(ref, data, ctx);
  if (!s) return null;
  const d = detailFrom(data);
  const p = propertyOf(ctx, s.listing_id, d);
  return { s, d, listing_name: p ? p.name : s.listing_id };
};
registerAction({
  key: 'message', inspector: 'stay', label: 'Message', icon: '💬', order: 10, primaryIn: ['stays', 'guests'],
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).message; },
  run: (ref, data, ctx) => {
    const c = stayCtx(ref, data, ctx);
    if (c) openMessageSheet(ctx, { hid: c.s.id, guest: c.s.guest_name, listing_name: c.listing_name, check_in: c.s.check_in, check_out: c.s.check_out, channel: c.s.channel });
  },
});
registerAction({
  key: 'recovery', inspector: 'stay', label: 'Recovery', icon: '❤', order: 20,
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).recovery; },
  run: (ref, data, ctx) => {
    const c = stayCtx(ref, data, ctx);
    if (c) openRecoverySheet(ctx, { hid: c.s.id, guest: c.s.guest_name, listing_name: c.listing_name, check_in: c.s.check_in, check_out: c.s.check_out });
  },
});
registerAction({
  key: 'claim', inspector: 'stay', label: 'Claim', icon: '⚖', order: 30,
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).claim; },
  run: (ref, data, ctx) => {
    const c = stayCtx(ref, data, ctx);
    if (c) openClaimSheet(ctx, { listing_id: c.s.listing_id, listing_name: c.listing_name, reservation_id: c.s.id, guest: c.s.guest_name, check_in: c.s.check_in, check_out: c.s.check_out });
  },
});
registerAction({
  key: 'resolution', inspector: 'stay', label: 'Resolution', icon: '💸', order: 40,
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).resolution; },
  run: (ref, data, ctx) => {
    const c = stayCtx(ref, data, ctx);
    if (c) openResolutionSheet(ctx, { listing_id: c.s.listing_id, listing_name: c.listing_name, reservation_id: c.s.id, confirmation_code: confirmationOf(c.d), guest: c.s.guest_name, channel: c.s.channel, check_in: c.s.check_in, check_out: c.s.check_out });
  },
});

// ── listing actions: + Resolution / + Claim for the home itself ──
const listingName = (ref: EntityRef, ctx: CalendarCtx) => { const p = ctx.property(String(ref.id)); return p ? p.name : String(ref.id); };
registerAction({
  key: 'resolution', inspector: 'listing', label: '+ Resolution', icon: '💸', order: 60,
  when: (ref) => ref.kind === 'listing' && !!ref.id,
  run: (ref, _data, ctx) => openResolutionSheet(ctx, { listing_id: String(ref.id), listing_name: listingName(ref, ctx) }),
});
registerAction({
  key: 'claim', inspector: 'listing', label: '+ Claim', icon: '⚖', order: 61,
  when: (ref) => ref.kind === 'listing' && !!ref.id,
  run: (ref, _data, ctx) => openClaimSheet(ctx, { listing_id: String(ref.id), listing_name: listingName(ref, ctx) }),
});

// ── sections ──
for (const s of [specialOfferSection, recoverySection, messageSection, listingScoreSection, listingCountsSection, listingLinksSection]) registerSection(s);

// ── busy + css ──
registerBusy('guest', () => sheetBusy());
registerCss('guest', GUEST_CSS);

// for tests / the harness
export { soFor };
